import React from 'react';
import Header from './shared/Header';
import Footer from './Footer';
import SEO from './SEO';
import {
  BuildingOfficeIcon,
  ChartBarIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/20/solid';

const providers = [
  {
    name: 'BlackRock',
    description: 'ETF-based model portfolios with periodic rebalancing',
    strengths: ['Large AUM', 'ETF expertise', 'Global reach'],
    weaknesses: ['Passive approach', 'Limited customization', 'Market tracking']
  },
  {
    name: 'Russell Investments',
    description: 'Multi-asset class strategies with tactical allocation',
    strengths: ['Multi-asset expertise', 'Research-driven', 'Tactical allocation'],
    weaknesses: ['Higher fees', 'Complex strategies', 'Limited transparency']
  },
  {
    name: 'JPMorgan Asset Management',
    description: 'Institutional-grade strategies for advisors',
    strengths: ['Institutional quality', 'Global research', 'Diversified offerings'],
    weaknesses: ['High minimums', 'Complex products', 'Limited flexibility']
  },
  {
    name: 'iShares',
    description: 'ETF-focused model portfolios with passive strategies',
    strengths: ['Low cost', 'ETF expertise', 'Broad diversification'],
    weaknesses: ['Passive only', 'Limited alpha', 'Market correlation']
  },
  {
    name: 'SharpeMetrix',
    description: 'Alternative data-driven systematic strategies',
    strengths: ['Alternative data', 'Lower fees', 'Research-driven', '100% Systematic'],
    weaknesses: ['Equities Focused']
  }
];

export default function TAMPComparisonPage() {
  return (
    <>
      <SEO 
        title="TAMP Comparison: SharpeMetrix vs Major Providers"
        description="Compare SharpeMetrix with leading TAMP providers including BlackRock, Russell Investments, JPMorgan, and iShares to understand key differences in approach and value."
        keywords="TAMP comparison, BlackRock model portfolios, Russell Investments, JPMorgan Asset Management, iShares, SharpeMetrix, turnkey asset management"
        url="/tamp-comparison"
      />
      <Header />
      <div className="min-h-screen bg-bg-100">
        <main className="relative isolate">
          {/* Background Gradient */}
          <div
            className="absolute inset-x-0 top-4 -z-10 flex transform-gpu justify-center overflow-hidden blur-3xl"
            aria-hidden="true"
          >
            <div
              className="aspect-[1108/632] w-[69.25rem] flex-none bg-gradient-to-r from-[#305CFE] to-[#02D3FF] opacity-20"
              style={{
                clipPath:
                  'polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)',
              }}
            />
          </div>

          {/* Header Section */}
          <div className="px-6 pt-14 lg:px-8">
            <div className="mx-auto max-w-3xl pt-24 text-center sm:pt-40 pb-16 sm:pb-24">
              <h1 className="text-4xl font-bold tracking-tight text-text-100 sm:text-6xl">
                TAMP Comparison: SharpeMetrix vs Major Providers
              </h1>
              <p className="mt-6 text-lg leading-8 text-text-200">
                Compare SharpeMetrix with leading Turnkey Asset Management Program (TAMP) providers to understand the key differences in approach, methodology, and value proposition.
              </p>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="mx-auto max-w-7xl px-6 lg:px-8 pb-24 sm:pb-32">

            {/* Overview Section */}
            <section className="mb-16 lg:mb-24">
              <div className="mx-auto max-w-3xl text-center">
                <h2 className="text-3xl font-bold tracking-tight text-text-100 sm:text-4xl flex items-center justify-center gap-x-3">
                  <BuildingOfficeIcon className="h-8 w-8 text-primary-100" aria-hidden="true" />
                  Major TAMP Providers
                </h2>
                <p className="mt-6 text-lg leading-8 text-text-200">
                  Each TAMP provider offers different approaches to investment management. Understanding these differences helps advisors choose the right solution for their practice and clients.
                </p>
              </div>
            </section>

            {/* Provider Comparison Section */}
            <section className="mb-16 lg:mb-24">
              <h2 className="text-3xl font-semibold leading-9 tracking-tight text-text-100 mb-10 text-center">
                Provider Comparison
              </h2>
              <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 xl:grid-cols-3">
                {providers.map((provider, index) => (
                  <div key={provider.name} className={`bg-bg-200 rounded-lg p-6 shadow-lg ${provider.name === 'SharpeMetrix' ? 'border-2 border-primary-100' : ''}`}>
                    <div className="flex items-center mb-4">
                      <h3 className="text-lg font-semibold text-text-100">{provider.name}</h3>
                    </div>
                    
                    <p className="text-sm text-text-200 mb-4">{provider.description}</p>
                    
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-text-100 mb-2">Strengths:</h4>
                      <ul className="text-xs text-text-200 space-y-1">
                        {provider.strengths.map((strength, idx) => (
                          <li key={idx} className="flex items-center">
                            <CheckCircleIcon className="text-green-400 mr-2 h-3 w-3" />
                            {strength}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-text-100 mb-2">Limitations:</h4>
                      <ul className="text-xs text-text-200 space-y-1">
                        {provider.weaknesses.map((weakness, idx) => (
                          <li key={idx} className="flex items-center">
                            <XCircleIcon className="text-red-400 mr-2 h-3 w-3" />
                            {weakness}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="mt-6">
                      {provider.name === 'SharpeMetrix' ? (
                        <a
                          href="/register"
                          className="inline-flex items-center text-sm font-semibold text-primary-100 hover:text-primary-200"
                        >
                          Start free trial <span aria-hidden="true">→</span>
                        </a>
                      ) : (
                        <a
                          href={`/${provider.name.toLowerCase().replace(/\s+/g, '-')}`}
                          className="inline-flex items-center text-sm font-semibold text-primary-100 hover:text-primary-200"
                        >
                          Learn More <span aria-hidden="true">→</span>
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* SharpeMetrix Advantages Section */}
            <section className="bg-bg-200/60 rounded-xl p-8 lg:p-12 mb-16 lg:mb-24 ring-1 ring-white/10 shadow-lg">
              <h2 className="text-3xl font-semibold leading-9 tracking-tight text-text-100 mb-10 text-center">
                The SharpeMetrix Advantage
              </h2>
              <div className="grid grid-cols-1 gap-x-8 gap-y-12 lg:grid-cols-3 lg:gap-y-16">
                <div className="flex flex-col items-start">
                  <h3 className="text-xl font-semibold leading-7 text-text-100 mb-4">
                    Alternative Data Approach
                  </h3>
                  <p className="text-base leading-7 text-text-200">
                    Unlike traditional TAMPs that rely on conventional market data, SharpeMetrix leverages proprietary alternative data signals including earnings call sentiment, patent analysis, and analyst quality metrics to identify unique investment opportunities.
                  </p>
                </div>
                
                <div className="flex flex-col items-start">
                  <h3 className="text-xl font-semibold leading-7 text-text-100 mb-4">
                    Lower Cost Structure
                  </h3>
                  <p className="text-base leading-7 text-text-200">
                    Our systematic approach and technology-driven processes enable us to offer institutional-quality investment management at a fraction of the cost of traditional TAMP providers.
                  </p>
                </div>
                
                <div className="flex flex-col items-start">
                  <h3 className="text-xl font-semibold leading-7 text-text-100 mb-4">
                    100% Systematic
                  </h3>
                  <p className="text-base leading-7 text-text-200">
                    Our rules-based approach eliminates human bias and emotional decision-making, providing consistent, repeatable investment processes that can scale with your practice.
                  </p>
                </div>
              </div>
            </section>

            {/* CTA Section */}
            <section className="bg-bg-200/60 rounded-xl p-8 lg:p-12 ring-1 ring-white/10 shadow-lg">
              <div className="mx-auto max-w-3xl text-center">
                <h2 className="text-3xl font-bold tracking-tight text-text-100 sm:text-4xl flex items-center justify-center gap-x-3">
                  <ChartBarIcon className="h-8 w-8 text-primary-100" aria-hidden="true" />
                  Ready to Experience the SharpeMetrix Difference?
                </h2>
                <p className="mt-6 text-lg leading-8 text-text-200">
                  See how our alternative data approach and systematic methodology can transform your investment management capabilities.
                </p>
                <div className="mt-10 flex items-center justify-center gap-x-6">
                  <a
                    href="/register"
                    className="inline-flex items-center gap-x-2 rounded-md bg-primary-100 px-4 py-3 text-sm font-semibold text-text-100 shadow-sm hover:bg-primary-200"
                  >
                    Start Free 30-Day Trial
                  </a>
                  <a href="/what-is-tamp" className="text-sm font-semibold leading-6 text-text-200">
                    Learn About TAMPs <span aria-hidden="true">→</span>
                  </a>
                </div>
              </div>
            </section>
          </div>
        </main>
      </div>
      <Footer />
    </>
  );
} 